import { Star } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { ProductImageFallback } from './ProductImageFallback';
import { Badge } from './ui/Badge';

import { ROUTE_PATHS } from '@/config/constants';
import { useAuth } from '@/contexts/AuthContext';
import { cn, mountProductTags, priceFormatterV2 } from '@/lib/utils';
import { Product } from '@/services/ProductsService';

interface ProductCardProps {
  product: Product;
  score?: number;
}

export function ProductCard({ product, score }: ProductCardProps) {
  const [imageError, setImageError] = useState(false);

  const { user } = useAuth();

  const formattedPrice = parseFloat(product.price);
  const formattedSalePrice = parseFloat(product.salePrice);

  const tags = mountProductTags(product);
  const pairsWith = ['Pairs With', 'Compre o Look', 'Compra el Look'];

  return (
    <Link
      to={ROUTE_PATHS.VISION_PRODUCT.replace(':productId', String(product.id))}
      className="flex flex-col rounded-lg border border-solid border-[#E0E0E0]"
    >
      <div className="relative">
        {tags && tags.length > 0 && (
          <ul className="absolute left-2 top-2 z-10">
            {tags.map((tag) => (
              <li key={tag}>
                <Badge
                  className={`${pairsWith.includes(tag) ? 'bg-green-500' : 'bg-red-500'}`}
                >
                  {tag}
                </Badge>
              </li>
            ))}
          </ul>
        )}

        {imageError && <ProductImageFallback />}

        {!imageError && (
          <>
            {product.fashionaiVideo && (
              <div>
                <video autoPlay muted loop playsInline>
                  <source src={product.fashionaiVideo} type="video/mp4" />
                </video>
              </div>
            )}

            {!product.fashionaiVideo && (
              <>
                <img
                  src={product.image1}
                  alt={product.name}
                  className="rounded-t-lg"
                  onError={() => setImageError(true)}
                />

                {product.image2 && (
                  <img
                    src={product.image2}
                    alt={product.name}
                    className="absolute left-0 top-0 h-full w-full rounded-t-lg opacity-0 transition-opacity duration-500 hover:opacity-100"
                    onError={() => setImageError(true)}
                  />
                )}
              </>
            )}
          </>
        )}

        {score && (
          <div className="absolute right-2 top-2 flex items-center gap-1 rounded-md bg-background/80 px-2 py-1 text-foreground backdrop-blur-sm">
            <Star className="h-4 w-4 fill-primary" />
            {(score * 100).toFixed(0)}%
          </div>
        )}
      </div>

      <div className="h-20 flex-col p-5 text-xs 2xl:text-sm">
        <h3 className="block truncate font-light">{product.name}</h3>

        {formattedPrice > 0 && formattedSalePrice > 0 && (
          <div className="flex gap-2 font-light">
            {formattedSalePrice < formattedPrice && (
              <span className="line-through opacity-50">
                {priceFormatterV2(
                  formattedPrice,
                  user!.customer.language,
                  user!.customer.platform,
                )}
              </span>
            )}

            <span
              className={cn(
                'opacity-90',
                formattedSalePrice < formattedPrice &&
                  'font-semibold text-red-400',
              )}
            >
              {priceFormatterV2(
                formattedSalePrice,
                user!.customer.language,
                user!.customer.platform,
              )}
            </span>
          </div>
        )}
      </div>
    </Link>
  );
}
